<template>
  <div>
    <slot />
  </div>
</template>

<script setup lang="ts">
import type GeoJSON from 'geojson'
import { useMap } from '../mapbox/context'
import { onBeforeMount, onUnmounted, provide, watch } from 'vue'

const { map } = useMap()

const props = defineProps<{
  sourceId: string
  // Due to Vue <= 3.2 limitation, it's not possible to declare a props with custom type imported from another file
  // In this case, `data` should be of type
  // GeoJSON.Feature<GeoJSON.Geometry>| GeoJSON.FeatureCollection<GeoJSON.Geometry> | string
  // When we upgrade to Vue 3.3, we could change it back
  // https://github.com/vuejs/core/discussions/6737
  data:
    | object // GeoJSON.Feature<GeoJSON.Geometry> | GeoJSON.FeatureCollection<GeoJSON.Geometry>
    | string
  cluster?: boolean
  clusterMaxZoom?: number
  clusterRadius?: number
  clusterProperties?: object
  generateId?: boolean
  buffer?: number
}>()

type DataType =
  | GeoJSON.Feature<GeoJSON.Geometry>
  | GeoJSON.FeatureCollection<GeoJSON.Geometry>
  | string

const setDataSource = (data: DataType) => {
  const source = map.value?.getSource(props.sourceId)

  if (data && source && source.type === 'geojson') {
    source.setData(data)
  } else {
    // map.addSource has some weird issues with passing in undefined
    // for some property values. JSON.parse/stringify loses the
    // undefined properties, and solves that issue.
    map.value?.addSource(
      props.sourceId,
      JSON.parse(
        JSON.stringify({
          type: 'geojson',
          data: data,
          generateId: props.generateId ?? false,
          buffer: props.buffer ?? 0,
          cluster: props.cluster ?? false,
          clusterMaxZoom: props.clusterMaxZoom,
          clusterRadius: props.clusterRadius,
          clusterProperties: props.clusterProperties,
        }),
      ),
    )
  }
}

onBeforeMount(() => {
  setDataSource(props.data as DataType)
})

watch(
  () => props.data,
  (d) => setDataSource(d as DataType),
)

const refreshData = () => {
  const source = map.value?.getSource(props.sourceId)
  if (source && source.type === 'geojson') {
    source.setData(props.data as DataType)
  }
}

onUnmounted(() => {
  map.value?.removeSource(props.sourceId)
})

provide('source', props.sourceId)

defineExpose({ refreshData })
</script>
